import { useState } from 'react';
import { Calculator, DollarSign, Info, MapPin, Users, Luggage } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { taxiRates, calculateFare, getAllLocations } from '../data/taxiRates';

const Pricing = () => {
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [passengers, setPassengers] = useState('2');
  const [extraBags, setExtraBags] = useState('0');
  const [fareEstimate, setFareEstimate] = useState(null);

  const locations = getAllLocations();

  const handleCalculate = () => {
    if (!pickup || !dropoff) {
      return;
    }

    const estimate = calculateFare(
      pickup,
      dropoff,
      parseInt(passengers),
      parseInt(extraBags)
    );

    setFareEstimate(estimate);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="relative h-[300px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1562036861-44811e0b4481?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxiYWhhbWFzJTIwYmVhY2h8ZW58MHx8fHwxNzc0Mzk4ODIzfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Taxi Rates</h1>
          <p className="text-xl">Official government-regulated zone rates for The Bahamas</p>
        </div>
      </section>

      {/* Fare Calculator */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-xl border-t-4 border-red-600 mb-12">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Calculator className="w-8 h-8 text-red-600" />
                  <CardTitle className="text-3xl">Fare Calculator</CardTitle>
                </div>
                <p className="text-gray-600 mt-2">
                  Estimate your fare using official Bahamas taxi zone rates
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="pickup">Pickup Location</Label>
                    <Select value={pickup} onValueChange={setPickup}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select pickup location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="dropoff">Drop-off Location</Label>
                    <Select value={dropoff} onValueChange={setDropoff}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select drop-off location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="passengers">Number of Passengers</Label>
                    <Select value={passengers} onValueChange={setPassengers}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7].map(num => (
                          <SelectItem key={num} value={num.toString()}>{num} Passenger{num > 1 ? 's' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="extraBags">Extra Luggage (beyond 2 bags/person)</Label>
                    <Input
                      id="extraBags"
                      type="number"
                      min="0"
                      value={extraBags}
                      onChange={(e) => setExtraBags(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleCalculate}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                  disabled={!pickup || !dropoff}
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Calculate Fare
                </Button>

                {/* Fare Estimate Display */}
                {fareEstimate && (
                  <div className="mt-8">
                    {fareEstimate.found ? (
                      <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6">
                        <h3 className="text-2xl font-bold text-green-900 mb-4">Estimated Fare</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center text-lg">
                            <span className="text-gray-700">Base Fare (2 passengers):</span>
                            <span className="font-semibold">${fareEstimate.baseFare.toFixed(2)}</span>
                          </div>
                          {fareEstimate.extraPassengerFee > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Extra Passengers:</span>
                              <span className="font-semibold">${fareEstimate.extraPassengerFee.toFixed(2)}</span>
                            </div>
                          )}
                          {fareEstimate.extraBagFee > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Extra Luggage:</span>
                              <span className="font-semibold">${fareEstimate.extraBagFee.toFixed(2)}</span>
                            </div>
                          )}
                          {fareEstimate.tollFee > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Paradise Island Bridge Toll:</span>
                              <span className="font-semibold">${fareEstimate.tollFee.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="border-t-2 border-green-600 pt-3 mt-3">
                            <div className="flex justify-between items-center text-2xl">
                              <span className="font-bold text-green-900">Total:</span>
                              <span className="font-bold text-green-900">${fareEstimate.total.toFixed(2)} {fareEstimate.currency}</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mt-4">
                          *Fare is based on official Bahamas government-regulated zone rates. BSD (Bahamian Dollar) = USD 1:1
                        </p>
                      </div>
                    ) : (
                      <div className="bg-amber-50 border-2 border-amber-500 rounded-lg p-6">
                        <p className="text-amber-900 font-semibold">
                          Rate not found for this route. Please contact us at +1242-525-8675 for a custom quote.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Rate Tables */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Airport Rates */}
              <Card>
                <CardHeader className="bg-red-50">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-6 h-6 text-red-600" />
                    Airport Fares
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-600 mb-4">From Nassau International Airport (LPIA)</p>
                  <div className="space-y-2">
                    {taxiRates.airportRates.map((route, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b">
                        <span className="text-sm">{route.destination}</span>
                        <span className="font-semibold text-red-600">${route.rate}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-4">
                    *Base fare includes 2 passengers and 2 bags per person
                  </p>
                </CardContent>
              </Card>

              {/* Additional Charges */}
              <Card>
                <CardHeader className="bg-red-50">
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-6 h-6 text-red-600" />
                    Additional Charges
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Extra Passenger (beyond 2)</span>
                      <span className="font-semibold text-red-600">+${taxiRates.extraPassengerFee}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Extra Luggage (per bag)</span>
                      <span className="font-semibold text-red-600">+${taxiRates.extraLuggageFee}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Golf Bag</span>
                      <span className="font-semibold text-red-600">+${taxiRates.golfBagFee}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Paradise Island Bridge Toll</span>
                      <span className="font-semibold text-red-600">+${taxiRates.paradiseBridgeToll}</span>
                    </div>
                    <div className="flex justify-between items-center border-t pt-4">
                      <span className="text-sm font-semibold">Island Tours (Hourly)</span>
                      <span className="font-semibold text-red-600">${taxiRates.islandTourRate}/hr</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Stops/Waiting Time</span>
                      <span className="font-semibold text-red-600">${taxiRates.waitingCharge}/min</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-4 pt-4 border-t">
                      *Waiting time charged for stops at grocery stores, liquor stores, etc.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Important Information */}
            <Card className="bg-blue-50 border-blue-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <Info className="w-6 h-6" />
                  Important Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-blue-900">
                  <li className="flex items-start gap-2">
                    <span className="font-bold mt-1">•</span>
                    <span>All rates are government-regulated official zone rates set by the Ministry of Public Works</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold mt-1">•</span>
                    <span>Rates are the same for all vehicle types (sedan, van, or SUV)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold mt-1">•</span>
                    <span>Payment: Cash (BSD or USD) accepted. Credit cards may be available - please confirm when booking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold mt-1">•</span>
                    <span>Tipping: 10-15% is customary for good service (~$5-10 for airport runs)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold mt-1">•</span>
                    <span>All fares should be confirmed before departure to avoid disputes</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
